import {gsap} from "gsap";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {BruteForce} from "../BruteForce";
import {BruteForceGameAssets} from "../BruteForceGameAssets";
import {ParticleController} from "../components/particles/ParticleController";
import {ParticleSettings} from "../components/particles/ParticleSettings";
import {BruteForceSound} from "../sound/BruteForceSound";
import {BruteForceHelper} from "../utils/BruteForceHelper";
import {DisplayObject} from "pixi.js";
import {BelowStakeWinRestriction} from "../server/BelowStakeWinRestriction";

export class BruteForceBigWinView extends PIXI.Container {
    private _countUp: BasicBitmapCountUp;
    private _spines: PIXI.spine.Spine[];
    private _spineNameTags: PIXI.Sprite[];
    private _spineStamps: PIXI.Sprite[];
    private _bigwinFrame: PIXI.Sprite;
    private _fountainContainer: PIXI.Container;
    private _particleEffectsLoops: ParticleController[] = [];
    private _flashOverlay: PIXI.Sprite;

    constructor() {
        super();
        this.name = "BIG_WIN";
        this._spines = [];
        this._spineNameTags = [];
        this._spineStamps = [];
        this.init();
        this.position.set(0, 30);
    }

    private _maxLevel: number;

    public set maxLevel(value: number) {
        this._maxLevel = value;
    }

    public show(duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        this.alpha = 0;
        this.visible = true;
        this._countUp.position.set(360, 320);
        this.initFountains(this._fountainContainer);
        tl.add(() => {
            this._flashOverlay.visible = true;
            this._countUp.setAmount(0);
        }, 0);
        tl.to(this, {duration: duration, alpha: 1}, 0);
        return tl;
    }

    public hide(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            BruteForceSound.playBigWinOutro();
            if (!BruteForce.model.brokeTheBank) {
                BruteForce.keypadPlugin.setWin(BruteForce.model.gameData.accumulatedRoundWin, true, BelowStakeWinRestriction.isWinBelowStake());
                BruteForceSound.fadeMusic(1, 0.5);
            } else {
                BruteForceSound.switchMusic({name: BruteForceSound.WINCAP_MUSIC});
            }
        }, 0);
        tl.add(this.getCountUpScaleAnimation(0.25, "power2.in", 2.7));
        tl.add(this.getCountUpScaleAnimation(0.3, "power2.in", 2));
        tl.to(this._countUp, {duration: 0.2, y: 320});
        tl.add(BruteForce.reelScreenDarkOverlay.hideOverlay());
        tl.to(this, {duration: 0.5, alpha: 0});
        tl.add(() => {
            this.removeParticleFountain();
            this.visible = false;
            this._countUp.visible = false;
            this._flashOverlay.visible = false;
            this.resetSpineArray();
        }, tl.totalDuration());

        return tl;
    }

    public startCountUp(upto: number): gsap.core.Timeline {
        const startCountTl: gsap.core.Timeline = gsap.timeline();
        startCountTl.add(() => {
            this._countUp.visible = true;
            this._countUp.scale.set(1.4, 1.4);
            this.addChild(this._countUp);
        }, 0);
        startCountTl.add(this._countUp.getCountAnimation(0, upto, 1));
        startCountTl.to(this._countUp.scale, {duration: 0.25, x: 1.9, y: 1.9}, "<0.9");
        startCountTl.to(this._countUp.position, {duration: 0.5, y: 600}, "<");
        startCountTl.to(this._countUp.scale, {
            duration: 0.1,
            x: 1.6,
            y: 1.6,
            ease: "power2.in"
        }, "<0.25");
        startCountTl.to(this._countUp.scale, {
            duration: 0.05,
            x: 1.75,
            y: 1.75,
            ease: "power2.out"
        }, ">");
        startCountTl.to(this._countUp.scale, {
            duration: 0.05,
            x: 1.6,
            y: 1.6,
            ease: "power2.in"
        }, ">");

        // console.log(startCountTl.totalDuration());
        return startCountTl;
    }

    public getLevelAnimation(countFrom: number, countTo: number, duration: number, level: number): gsap.core.Timeline {
        Logger.logDev("getLevelAnimation called details :: from : " + countFrom + " to : " + countTo + " duration : " + duration + " level : " + level);
        const tl: gsap.core.Timeline = gsap.timeline(),
            spine: PIXI.spine.Spine = this.getSpine(level),
            nameTag: PIXI.Sprite = this.getLevelNameTag(level),
            stamp: PIXI.Sprite = this.getLevelStamp(level),
            previousLevel: number = JSON.parse(JSON.stringify(level - 1)),
            isLastLevel: boolean = (level === this._maxLevel);

        tl.add(() => {
            /* if (previousLevel !== 0) {
                 this._spines[previousLevel - 1].visible = false;
             }*/
            this.addChild(spine);
            this.addChild(nameTag);
            this.addChild(stamp);
            this.addChild(this._fountainContainer);
            this.addChild(this._countUp);
            if (level === 1 && countFrom === countTo) {
                BruteForceSound.playTrack(BruteForceSound.BIG_WIN_LEVEL_1_NO_COUNTUP, 1);
            } else {
                BruteForceSound.playTrack(BruteForceSound.BIG_WIN_LEVELS[level - 1], 1);
            }
            BruteForce.foregroundElements.playArcadeMachineAnimation("bw", false);

            // BruteForce.foregroundElements.emitSplash(level);
        }, 0);

        const scaleTl: gsap.core.Timeline = gsap.timeline(),
            delay: number = 0;
        if (level !== 1) {
            scaleTl.add(this.getCountUpScaleAnimation(0.25, "power2.in", 2 + previousLevel * 0.3), 0)
            scaleTl.add(this.getCountUpScaleAnimation(0.1, "power2.in", 1.6 + previousLevel * 0.3), ">");
            scaleTl.add(this.getCountUpScaleAnimation(0.05, "power2.out", 1.75 + previousLevel * 0.3), ">");
            scaleTl.add(this.getCountUpScaleAnimation(0.05, "power2.in", 1.6 + previousLevel * 0.3), ">");
        }
        scaleTl.to(nameTag, {duration: 0.25, alpha: 1}, 0);
        scaleTl.to(nameTag, {duration: 0.25, x: 165, ease: "back.in"}, "<");
        scaleTl.to(nameTag, {duration: 0.1, x: 140, ease: "none"}, ">");
        scaleTl.to(nameTag, {duration: 0.05, x: 155, ease: "power2.out"}, ">");
        //console.log(this._countUp.scale);

        // scaleTl.add(this.getCountUpScaleAnimation(0.25, "power2.in", 1.2 + (level) * 0.3));
        scaleTl.to(spine, {duration: 0.2, alpha: 1}, delay);
        scaleTl.add(BruteForce.bigWinView.strikeWhiteFlash(), "<");
        scaleTl.add(BruteForceHelper.playSpineWithTimeline(spine, 0, "bw_" + level), "<");

        scaleTl.add(this.getCountUpScaleAnimation(duration - 1.25, "power2.in", 1.2 + (previousLevel * 0.3)), "<0.55");
        scaleTl.to(stamp, {duration: 0.15, alpha: 1}, 2);
        scaleTl.to(stamp.scale, {duration: 0.15, x: 1, y: 1, ease: "power3.in"}, "<0.1");
        scaleTl.add(() => {
            this.emitFountain(level);
            BruteForce.gameContainer.screenShake(0.3, 150, false, 15);
        }, ">");
        tl.add(scaleTl, delay);

        tl.add(this._countUp.getCountAnimation(countFrom, countTo, duration - 0.1, "none", level), delay + 0.1);
        if (level !== this._maxLevel) {
            tl.add(this.Shake({
                obj: this._countUp,
                x: 1,
                y: 1,
                hertz: 70,
                Intensity: 3,
                duration: duration - 2.5
            }), tl.totalDuration() - 2.5);
        }
        tl.add(() => {
            stamp.visible = false;
            nameTag.visible = false;
        });
        // console.log(tl.totalDuration());
        return tl;
    }

    public onAbort(level: number): void {
//stop sound
        if (BruteForceSound.soundPlayer.fetchSoundPlayer(BruteForceSound.BIG_WIN_LEVEL_1_NO_COUNTUP)?.isPlaying) {
            BruteForceSound.stopSound(BruteForceSound.BIG_WIN_LEVEL_1_NO_COUNTUP);
        } else {
            BruteForceSound.stopSound(BruteForceSound.BIG_WIN_LEVELS[level]);
        }
        this.hideParticleFountain();
    }

    public emitFountain(lvl: number): void {
        this._particleEffectsLoops.forEach((p: ParticleController) => {
            p.enableEmit(false);
        });

        if (lvl === 2) {
            this._particleEffectsLoops[0].enableEmit(true);
        } else if (lvl === 3) {
            this._particleEffectsLoops[0].enableEmit(true);
            this._particleEffectsLoops[1].enableEmit(true);
        } else if (lvl === 4) {
            this._particleEffectsLoops[0].enableEmit(true);
            this._particleEffectsLoops[1].enableEmit(true);
            this._particleEffectsLoops[2].enableEmit(true);
        } else if (lvl === 5) {
            this._particleEffectsLoops[0].enableEmit(true);
            this._particleEffectsLoops[1].enableEmit(true);
            this._particleEffectsLoops[2].enableEmit(true);
            this._particleEffectsLoops[3].enableEmit(true);
        }

    }

    public strikeWhiteFlash(): gsap.core.Timeline {
        const flashTl: gsap.core.Timeline = gsap.timeline();
        flashTl.add(() => {
            this.addChild(this._flashOverlay);
        }, 0)
        flashTl.to(this._flashOverlay, {alpha: 0.6, duration: 0.15});
        flashTl.to(this._flashOverlay, {alpha: 0, duration: 0.15});
        return flashTl;
    }

    private hideParticleFountain(): void {
        this._particleEffectsLoops.forEach((p: ParticleController) => {
            p.enableEmit(false);
        });
    }

    private removeParticleFountain(): void {
        this._particleEffectsLoops.forEach((p: ParticleController) => {
            p.enableEmit(false);
        });
        this._particleEffectsLoops.forEach((particleController) => {
            particleController.remove();
        });
        this._particleEffectsLoops = [];
    }

    private init(): void {


        this._countUp = new BasicBitmapCountUp({
            fontName: BruteForceGameAssets.BITMAP_BIGWIN_FONT,
            fontSize: 30,
            tint: 0xffffff
        }, 250, new PIXI.Point(0.5, 0.5));
        this._countUp.position.set(360, 600);
        this._countUp.stopAt(0);
        this.addChild(this._countUp);
        this.visible = false;
        this.alpha = 0;
        this._countUp.visible = false;

        this._fountainContainer = new PIXI.Container();
        this._fountainContainer.name = "fountainContainer";

        this.addChild(this._fountainContainer);


        this._flashOverlay = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._flashOverlay.name = "whiteFlashOverlay";
        this._flashOverlay.tint = 0xFFFFFF;
        this._flashOverlay.alpha = 0;
        this._flashOverlay.visible = false;
        this._flashOverlay.width = 597;
        this._flashOverlay.height = 477;
        this._flashOverlay.position.set(360, 245);
        this._flashOverlay.anchor.set(0.5, 0.5);
        this.addChild(this._flashOverlay);
    }

    private getSpine(level: number): PIXI.spine.Spine {
        const spine: PIXI.spine.Spine = new PIXI.spine.Spine(BruteForce.assetLoader.getSpineAsset("BW" + level.toString()));
        spine.position.set(360, 240);
        spine.name = "BIGWIN_SPINE_LEVEL_" + level;
        this._spines.push(spine);
        spine.alpha = 0;
        return spine;
    }

    private getLevelNameTag(level: number): PIXI.Sprite {
        const bigwinName: PIXI.Sprite = new PIXI.Sprite(BruteForce.assetLoader.getTextures("BW_text_" + level.toString())[0]);
        bigwinName.anchor.set(0.5, 0.5);
        bigwinName.position.set(-250, 515);
        bigwinName.name = "name" + level;
        this._spineNameTags.push(bigwinName);
        bigwinName.alpha = 0;

        return bigwinName;
    }

    private getLevelStamp(level: number): PIXI.Sprite {
        const bigwinStamp: PIXI.Sprite = new PIXI.Sprite(BruteForce.assetLoader.getTextures("BW_stamp_" + level.toString())[0]);
        bigwinStamp.anchor.set(0.5, 0.5);
        bigwinStamp.position.set(600, 500);
        bigwinStamp.name = "stamp" + level;
        this._spineStamps.push(bigwinStamp);
        bigwinStamp.alpha = 0;
        bigwinStamp.scale.set(4, 4);

        return bigwinStamp;
    }

    private getCountUpScaleAnimation(duration: number, ease: string, to: number, from?: number): gsap.core.Timeline {
        //  debugger;
        const tl: gsap.core.Timeline = gsap.timeline();
        if (from != undefined) {
            tl.add(() => {
                this._countUp.scale.set(from, from);
            });
        }
        tl.to(this._countUp.scale, {duration: duration, x: to, y: to, ease: ease});
        return tl;
    }

    private resetSpineArray(): void {

        if (this._spines.length > 0) {
            this.destroySpineAssets(this._spines);
        }

        if (this._spineStamps.length > 0) {
            this.destroySpineAssets(this._spineStamps);
        }
        if (this._spineNameTags.length > 0) {
            this.destroySpineAssets(this._spineNameTags);
        }

        this._spines = [];
        this._spineNameTags = [];
        this._spineStamps = [];

    }

    private destroySpineAssets(spineArray: PIXI.Sprite[] | PIXI.spine.Spine[]): void {
        spineArray.forEach((spineImg: PIXI.Sprite | PIXI.spine.Spine) => {
            if (spineImg) {
                spineImg.destroy();
            }
        });
    }

    private initFountains(container: PIXI.Container): void {
        this._particleEffectsLoops.push(BruteForce.particleManager.createAnimation(container, "coinSilverLoop", "coinSilver", ParticleSettings.coinsConfigs, true));

        this._particleEffectsLoops.push(BruteForce.particleManager.createAnimation(container, "coinGoldLoop", "coinGold", ParticleSettings.coinsConfigs, true));

        this._particleEffectsLoops.push(BruteForce.particleManager.createAnimation(container, "bulletLoop", "bullet", ParticleSettings.nonCoinConfigs, true));

        this._particleEffectsLoops.push(BruteForce.particleManager.createAnimation(container, "grenadeLoop", "grenade", ParticleSettings.nonCoinConfigs, true));

    }

    private Shake(config: IShakeConfig): gsap.core.Timeline {
        let startPosY: number = config.obj.y;
        let startPosX: number = config.obj.x;

        let t: boolean = true;
        const tl: gsap.core.Timeline = gsap.timeline({
            onStart: () => {
                startPosY = config.obj.y;
                startPosX = config.obj.x;
            },
            onUpdate: () => {
                const nt: number = tl.progress();
                const lt: number = tl.time();
                let et: number = nt;
                if (t) {
                    t = false;
                    return;
                }
                if (config.Intensity) {
                    if (config.y != 0)
                        config.obj.y = startPosY + Math.cos(lt * config.hertz) * (config.Intensity * et) * config.y;
                    if (config.x != 0)
                        config.obj.x = startPosX + (Math.sin(lt * config.hertz) * (config.Intensity * et)) * config.x;
                } else {
                    if (config.y != 0)
                        config.obj.y = startPosY + (Math.cos(lt * config.hertz) * et) * config.y;
                    if (config.x != 0)
                        config.obj.x = startPosX + (Math.sin(lt * config.hertz) * et) * config.x;
                }
            }
        });
        tl.to(this, {duration:config.duration})
        return tl;
    }


}

export interface IShakeConfig {
    x: number;
    y: number;
    obj: DisplayObject;
    duration: number;
    Intensity?: number;
    hertz: number;
}
